<template>
    <transition-group
        v-if="usedAnimationStyle !== 'NONE' && $store.state.triggerAnimation"
        appear
        @before-enter="beforeEnter"
        @enter="enter"
        :css="false"
    >
        <slot name="animationContent" />
    </transition-group>
    <template v-else>
        <slot name="animationContent" />
    </template>
</template>

<script>
    import gsap from "gsap";
    import ScrollTrigger from "gsap/ScrollTrigger";
    import { onMounted, onUnmounted, computed } from 'vue';
    import { useRoute } from 'vue-router';
    import { useStore } from "vuex";

    export default {
        name: 'animation-wrapper',
        components: {},
        props: {
            delayAnimation: {
                default: 0,
                type: Number,
            },
            animationStyle: {
                default: "NONE",
                type: String,
            },
            animationSpeed: {
                default: 0,
                type: Number,
            },
            disableAnimation: {
                default: false,
                type: Boolean,
            },
            isFinalSection: {
                default: false,
                type: Boolean,
            },
            animateOnMount: {
                default: false,
                type: Boolean,
            },
        },
        setup(props) {
            const route = useRoute();

            const isEditorPage = computed(() => {
                return route.fullPath.includes("/home/portfolios") &&
                    !route.fullPath.includes("/home/portfolios/create-new-portfolio") &&
                    route.fullPath !== "/home/portfolios";
            });

            const store = useStore();

            const animationSettings = computed(() => store.getters["design/animationSettings"]);

            const usedAnimationStyle = computed(() =>
                animationSettings.value?.animationStyle ?? props.animationStyle
            );

            const usedAnimationSpeed = computed(() =>
                animationSettings.value?.animationSpeed ?? props.animationSpeed
            );

            let context;
            gsap.registerPlugin(ScrollTrigger);
            const beforeEnter = (el) => {
                if (props.disableAnimation || usedAnimationStyle.value === "NONE") return;

                el.style.willChange = "transform";
                el.style.opacity = 0;

                // Lookup object instead of if-else chain
                const animationStyles = {
                    RISE: "translateY(10px)",
                    PAN_LEFT: "translateX(10px)",
                    PAN_RIGHT: "translateX(-10px)",
                    MOVE_IN: "translate(-10px, 10px)",
                    BRING_IN: "translate(-10px, -10px)",
                    FALL: "translateY(-10px)",
                    SCALE: "scale(0.98)",
                };

                if (animationStyles[usedAnimationStyle.value]) {
                    el.style.transform = animationStyles[usedAnimationStyle.value];
                }
            };

            const enter = (el) => {
                if (!props.disableAnimation && usedAnimationStyle.value !== "NONE") {
                    const start = () => {
                        if (props.isFinalSection) return "top bottom";

                        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
                        const maxScroll = ScrollTrigger.maxScroll(window);
                        const elementTop = el.getBoundingClientRect().top + window.scrollY;
                        const thresholdTop = maxScroll + viewportHeight - 120;

                        return elementTop > thresholdTop ? "top bottom" : "top bottom-=120px";
                    };

                    if (!el._hasAnimated) {
                        el._hasAnimated = false;
                    }

                    context = gsap.context(() => {
                        const animation = gsap.to(el, {
                            duration: usedAnimationSpeed.value,
                            delay: props.delayAnimation,
                            ease: "sine.out",
                            opacity: 1,
                            scale: 1,
                            x: "0",
                            y: "0",
                            paused: true,
                            clearProps: "transform,opacity"
                        });

                        if (props.animateOnMount) {
                            animation.play(0);
                            return;
                        }

                        ScrollTrigger.create({
                            trigger: el,
                            start: start,
                            once:true,
                            onEnter: () => {
                                animation.restart(true);
                            }
                        });
                    });
                }
            }

            onMounted(() => {
                ScrollTrigger.refresh(true);
            });

            onUnmounted(() => {
                if(context) {
                    context.kill();
                }
            });

            const updateAnimationSetting = () => {
                ScrollTrigger.refresh(true);
            }

            const recalculateTriggers = () => {
                setTimeout(() => {
                    ScrollTrigger.refresh(true);
                }, 10);
            }

            return {
                beforeEnter,
                enter,
                updateAnimationSetting,
                usedAnimationStyle,
                recalculateTriggers
            }
        },
        watch: {
            "$store.state.showSidePanel": function (newValue, oldValue) {
                if(newValue !== oldValue) {
                    this.recalculateTriggers();
                }
            },
            "$store.state.recalculateScrollTrigger": function (newValue, oldValue) {
                if(newValue !== oldValue) {
                    this.recalculateTriggers();
                }
            },
            "$store.state.portfolio.templateSettings.theme": function (newValue, oldValue) {
                if(newValue !== oldValue) {
                    this.recalculateTriggers();
                }
            },
        },
    }
</script>
